import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useState } from 'react';

import { Icon, Image, Spinner } from 'components';
import ActionMenu from 'components/ActionMenu';
import { ButtonV3 } from 'components/ComponentV2';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import { Button } from 'components/form';
import { Row } from 'components/layout';
import { renderFeatureNotification } from 'components/layouts/Header/Header.component';

import { ConfirmationScreen, useCampaigns } from 'hooks/useCampaigns.hook';
import { useNotification } from 'hooks/useNotification';

import { useJobs } from 'lib/contexts/hiring-context';
import { CompanyDashboardAttributes } from 'lib/models/company';
import {
  HorizontalPositionEnum,
  VerticalPositionEnum,
} from 'lib/models/notification';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';

import { JobState, JobStateOption } from '../useJobs';

import styles from './JobsTable.module.scss';

export const campaignsColumns = [
  {
    id: 1,
    field: 'title',
    label: 'Title',
    width: '50%',
  },
  {
    id: 2,
    field: 'status',
    label: 'Status',
    width: '15%',
  },
  {
    id: 3,
    field: '',
    label: 'Views',
    width: '15%',
  },
  {
    id: 4,
    field: '',
    label: 'Applications',
    width: '15%',
  },
  {
    id: 5,
    field: '',
    label: '',
    width: '5%',
  },
];

export interface JobsTableProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  companyDashboard: CompanyDashboardAttributes;
  mutateCompanyDashboard: () => void;
  companyId: string;
  companySlug: string;
}

export const JobsTable: React.FC<JobsTableProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    companyDashboard,
    mutateCompanyDashboard,
    companyId,
    companySlug,
  }: JobsTableProps) => {
    const contentClassNames = cn(
      {
        [styles.JobsTable]: true,
      },
      className
    );
    const notificationInstance = useNotification();
    const router = useRouter();
    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const [touchpoint, setTouchpoint] = useState<{
      id: string;
      title: string;
      state: JobState;
    } | null>(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState(false);
    const [isDeleteButtonLoading, setIsDeleteButtonLoading] = useState(false);
    const [confirmationScreenSelected, setConfirmationScreenSelected] =
      useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

    const [featuredIds, setFeaturedIds] = useState<string[]>([]);

    const {
      touchpoints,
      isLoading,
      size,
      setSize,
      hasMoreRecords,
      isLoadingMore,
      totalCount,
      getState,
      selectedState,
      setSelectedState,
      isAPILoading,
      onDelete,
      onCloseJob,
      mutateCampaigns,
    } = useJobs();

    const { onFeaturedToggle, getFeaturedIds } = useCampaigns();
    const { non_event_status_count_mapping } = companyDashboard || {};

    const onCloseJobHandler = async ({
      id,
      title,
    }: {
      id: string;
      title: string;
    }) => {
      try {
        if (id) {
          const response = await onCloseJob({
            state: JobState.CLOSED,
            enabled: false,
            id,
            title,
          });
          if (response) {
            mutateCompanyDashboard();
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
            setTouchpoint(null);
          }
        }
      } catch (error) {
        console.error(error);
        notificationInstance.handleExceptionError(error);
      }
    };

    const deleteConfirmationScreenDesc = touchpoint ? (
      <div>
        <p>
          Are you sure you want to delete{' '}
          <span style={{ fontWeight: 700 }}>{touchpoint.title}</span>?
        </p>
        <br />
        <p>All of the data and details associated with it will be gone.</p>
        <br />
        {touchpoint.state === JobState.CLOSED ? null : (
          <p>
            <button
              style={{
                background: 'none',
                color: 'blue',
                border: 'none',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '15px',
              }}
              onClick={() =>
                onCloseJobHandler({
                  id: touchpoint.id,
                  title: touchpoint.title,
                })
              }
            >
              Close
            </button>{' '}
            instead if you want to keep the data.
          </p>
        )}
      </div>
    ) : null;

    const ConfirmationScreenProps = [
      {
        id: ConfirmationScreen.DELETE,
        title: 'Delete Job',
        desc: deleteConfirmationScreenDesc,
        buttonContent: 'Yes, Delete',
        onCancel: () => {
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        },
        onConfirm: async () => {
          if (touchpoint) {
            try {
              setIsDeleteButtonLoading(true);

              const response = await onDelete(
                touchpoint.id as string,
                touchpoint.title as string
              );
              if (response) {
                mutateCompanyDashboard();
                setIsConfirmationModalOpen(false);
                setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
                setTouchpoint(null);
              }
              setIsDeleteButtonLoading(false);
            } catch (error) {
              console.error(error);
              notificationInstance.handleExceptionError(error);
            }
          }
        },
      },
    ];

    const getConfirmationModal = () => {
      const screen = ConfirmationScreenProps.find(
        (item) => item.id === confirmationScreenSelected
      );
      if (screen) {
        const { title, desc, buttonContent, onCancel, onConfirm } = screen;
        return (
          <Modal
            isOpen={isConfirmationModalOpen}
            onRequestClose={() => setIsConfirmationModalOpen(false)}
            isFullScreen={false}
          >
            <Confirmation
              title={title}
              desc={desc || ''}
              isLoading={isDeleteButtonLoading}
              buttonContent={buttonContent}
              onCancel={onCancel}
              onConfirm={onConfirm}
            />
          </Modal>
        );
      }
    };

    const getActionMenu = (
      id: string,
      title: string,
      touchpointable_type: TouchpointType,
      state: string,
      applications: number
    ) => {
      return [
        {
          id: '1',
          label: 'View',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            router.push(`${PAGE_ROUTES.HIRING}/${id}`);
          },
        },
        {
          id: '2',
          label: state !== JobState.CLOSED ? 'Edit' : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            let pathname = '';
            switch (touchpointable_type) {
              case TouchpointType.Job:
                pathname = `${PAGE_ROUTES.HIRING_EDIT.replace(
                  '[id]',
                  id
                )}?step=1&type=${TouchpointType.Job}`;
                break;
              case TouchpointType.Internship:
                pathname = `${PAGE_ROUTES.HIRING_EDIT.replace(
                  '[id]',
                  id
                )}?step=1&type=${TouchpointType.Internship}`;
                break;

              default:
            }
            router.push(pathname);
          },
        },
        {
          id: '3',
          label: state === JobState.LIVE ? 'Close' : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            onCloseJobHandler({ id, title });
          },
        },

        {
          id: '4',
          label: state !== JobState.LIVE ? 'Delete' : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setTouchpoint({ id, title, state: state as JobState });
            setIsConfirmationModalOpen(true);
            setConfirmationScreenSelected(ConfirmationScreen.DELETE);
          },
        },
        {
          id: '3',
          label:
            state === JobState.LIVE && applications > 0
              ? 'Review candidate'
              : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            router.push(
              `${PAGE_ROUTES.HIRING}/${id}?status=applied&showScreening=true&review=true`
            );
          },
        },
      ];
    };

    useEffect(() => {
      getFeaturedIds(companyId).then((data) => {
        setFeaturedIds(data);
      });
    }, [companyId]);

    const jobData = useMemo(() => {
      if (!isLoading && touchpoints && touchpoints.length === 0) {
        return (
          <div className={styles.noResults}>
            <div className={styles.noResultsContent}>
              <div className={styles.noResultsContentText}>
                <Image src="desert.png" source="aws" height="auto" width="98" />
                <p>Post your job or internship</p>
              </div>
              <ButtonV3
                onClick={() =>
                  router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)
                }
              >
                Start Hiring
              </ButtonV3>
            </div>
          </div>
        );
      } else {
        return (
          <table>
            <thead>
              <tr>
                {campaignsColumns.map((column) => (
                  <th key={column.id} style={{ width: column.width }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {touchpoints?.map((row) => {
                const {
                  id,
                  title,
                  applications,
                  state,
                  view_counter,
                  touchpointable_type,
                  unreviewed_applications_count,
                  apply_url,
                } = row;

                const opportunityState = getState(state as JobState);

                const isFeatureDisabled =
                  featuredIds.length >= 3 || opportunityState !== 'Live';

                const featured = featuredIds.includes(id);

                return (
                  <tr
                    key={id}
                    onClick={() => {
                      router.push(
                        {
                          pathname: `${PAGE_ROUTES.HIRING}/${id}`,
                          query: { selectedState },
                        },
                        `${PAGE_ROUTES.HIRING}/${id}`
                      );
                    }}
                  >
                    <td>
                      <div className={cn(styles.titleSection)}>
                        <p className={styles.title}>{title}</p>
                        {apply_url ? (
                          <Tooltip
                            content={
                              "This job is external. Enable easy apply to simplify the process, boost your job's visibility and get more applications."
                            }
                            position="top"
                            xAlign="center"
                            color="dark"
                            className={styles.externalLabelTooltip}
                            containerClassName={styles.titleSubSection}
                          >
                            External
                          </Tooltip>
                        ) : null}
                      </div>
                    </td>
                    <td>{opportunityState}</td>
                    <td>{view_counter}</td>
                    <td>
                      <Row align="center" columnGap={8}>
                        {applications}
                        {unreviewed_applications_count > 0 ? (
                          <p className={styles.new}>
                            {unreviewed_applications_count} new
                          </p>
                        ) : null}
                      </Row>
                    </td>
                    <td>
                      <div className={styles.actionButtonWrapper}>
                        {opportunityState === 'Live' &&
                          (featured ? (
                            <button
                              className={styles.featuredButton}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setLoading(true);
                                onFeaturedToggle({
                                  id,
                                  featured: !featured,
                                  companySlug,
                                })
                                  .then((res) => {
                                    notificationInstance.custom({
                                      message: '',
                                      timeout: 2500,
                                      position: {
                                        vertical: VerticalPositionEnum.TOP,
                                        horizontal:
                                          HorizontalPositionEnum.RIGHT,
                                      },
                                      renderContent: (
                                        fn?: (() => void) | undefined
                                      ) =>
                                        renderFeatureNotification(
                                          PAGE_ROUTES.COMPANY_PUBLIC.replace(
                                            '[slug]',
                                            companySlug
                                          ),
                                          'Job has been removed from featured!',
                                          fn
                                        ),
                                    });
                                    if (res) {
                                      const newIds = [...featuredIds];
                                      const index = newIds.indexOf(id);
                                      newIds.splice(index, 1);
                                      setFeaturedIds(newIds);
                                      mutateCampaigns();
                                    }
                                  })
                                  .finally(() => {
                                    setLoading(false);
                                  });
                              }}
                            >
                              <Icon
                                iconName="icon_star"
                                size="small"
                                className={styles.starIcon}
                              />
                            </button>
                          ) : (
                            <Tooltip
                              content={
                                isFeatureDisabled
                                  ? 'You can feature up to 3 opportunities'
                                  : 'Feature'
                              }
                              position="top"
                              xAlign="center"
                              color="dark"
                              className={cn(styles.featuredTooltip, {
                                [styles.featuredTooltipWidthAuto]:
                                  !isFeatureDisabled,
                              })}
                              containerClassName={
                                styles.featuredTooltipContainer
                              }
                            >
                              <button
                                className={cn(
                                  styles.featuredButton,
                                  styles.notFeatured
                                )}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setLoading(true);
                                  onFeaturedToggle({
                                    id,
                                    featured: !featured,
                                    companySlug,
                                  })
                                    .then((res) => {
                                      notificationInstance.custom({
                                        message: '',
                                        timeout: 2500,
                                        position: {
                                          vertical: VerticalPositionEnum.TOP,
                                          horizontal:
                                            HorizontalPositionEnum.RIGHT,
                                        },
                                        renderContent: (
                                          fn?: (() => void) | undefined
                                        ) =>
                                          renderFeatureNotification(
                                            PAGE_ROUTES.COMPANY_PUBLIC.replace(
                                              '[slug]',
                                              companySlug
                                            ),
                                            'Job has been featured!',
                                            fn
                                          ),
                                      });
                                      if (res) {
                                        const newIds = [...featuredIds];
                                        newIds.push(id);
                                        setFeaturedIds(newIds);
                                        mutateCampaigns();
                                      }
                                    })
                                    .finally(() => {
                                      setLoading(false);
                                    });
                                }}
                                disabled={isFeatureDisabled}
                              >
                                <Icon
                                  iconName={'icon_star-outline'}
                                  size="small"
                                />
                              </button>
                            </Tooltip>
                          ))}
                        <ActionMenu
                          options={getActionMenu(
                            id,
                            title,
                            touchpointable_type,
                            state,
                            applications
                          )}
                          triggerButtonClassName={styles.actionmenu}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
    }, [selectedState, touchpoints, featuredIds]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);
      if (router.query && router.query.selectedState !== undefined) {
        const index = Number(router.query.selectedState);
        setSelectedState(index);
      }

      return () => {
        endOfPageRef.current = null;
      };
    }, []);

    const [loading, setLoading] = useState(false);
    return (
      <div className={contentClassNames} style={style} data-testid="JobsTable">
        {loading && <Spinner showBackDrop={true} />}
        <Tab.Group
          selectedIndex={selectedState}
          onChange={(index) => {
            setSize(1);
            setSelectedState(index);
          }}
        >
          <Tab.List className={styles.tabList}>
            {JobStateOption.map((tab) => {
              const isValuePresent = non_event_status_count_mapping
                ? non_event_status_count_mapping[
                    tab.value as keyof typeof non_event_status_count_mapping
                  ]
                : false;
              return (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    cn(
                      styles.tab,
                      { [styles.tabSelected]: selected },
                      {
                        [styles.tabDisabled]: !isValuePresent && tab.id !== '1',
                      }
                    )
                  }
                >
                  {tab.label}
                </Tab>
              );
            })}
          </Tab.List>
          <Tab.Panels className={styles.tabPanels}>
            <Tab.Panel className={styles.tabPanel}>{jobData}</Tab.Panel>
            <Tab.Panel className={styles.tabPanel}>{jobData}</Tab.Panel>
            <Tab.Panel className={styles.tabPanel}>{jobData}</Tab.Panel>
            <Tab.Panel className={styles.tabPanel}>{jobData}</Tab.Panel>
            <Tab.Panel className={styles.tabPanel}>{jobData}</Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
        {(!isLoading && isLoadingMore) || isAPILoading ? (
          <Spinner size="small" className={styles.spinner} />
        ) : null}
        <Row align="center" className={styles.countsRow}>
          {touchpoints?.length ? (
            <p>
              Showing {touchpoints?.length} of {totalCount}
            </p>
          ) : null}
          {hasMoreRecords ? (
            <Button
              variant="text"
              onClick={() => {
                setSize(size + 1);
              }}
              className={styles.countsButton}
            >
              Show more
            </Button>
          ) : null}
          <div ref={endOfPageRef}></div>
        </Row>
        {isConfirmationModalOpen ? getConfirmationModal() : null}
      </div>
    );
  }
);

JobsTable.displayName = 'JobsTable';
